import { useState } from 'react';

import { Wrapper } from '@american-autonomy/ag-ui';

import DetailsViewEntityLink from 'components/DetailsViewEntityLink';
import SkeletonText from 'components/loaders/SkeletonText';
import CaptionText from 'components/text/CaptionText';

import useTranslation from './hooks/useTranslation';

import EntityMenu from './components/EntityMenu';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const DetailsViewEntityMenu: FunctionComponent<Props> = ({
    className = '',
    entityType,
    isLoading,
    items,
    onNext,
    testId = TEST_ID,
    totalCount
}) => {
    const {
        APPLICATION,
        BATTERY,
        BOUNDARY,
        CHEMICAL,
        CITY,
        CLASSIFICATION,
        CLIENT,
        CONTACT_NAME,
        CONTROLLER_SERIAL_NUMBER,
        CROP,
        DRONE,
        DRONE_SERIAL_NUMBER,
        EMAIL,
        EPA_NUMBER,
        ESTIMATED_ACRES,
        FAA_REGISTRATION_NUMBER,
        FARM,
        FIELD,
        FLIGHT_CONTROLLER_FIRMWARE_VERSION,
        FLIGHT_HOURS,
        FORMULATION,
        MANUFACTURER,
        MIX,
        MODE_OF_ACTION,
        NUMBER_OF_FLIGHTS,
        PHONE,
        PILOT,
        PPE,
        PPE_REENTRY,
        RAINFASTNESS,
        REI,
        ROLE,
        SCHEDULED_PILOT,
        SERIAL_NUMBER,
        SIGNAL_WORD,
        STATE,
        STATUS,
        TARGET_PEST,
        TOTAL_ACRES,
        TOTAL_AREA_APPLIED,
        TOTAL_VOLUME_APPLIED,
        USER,
        WARRANTY_END_DATE,
        WARRANTY_REMAINING,
        WARRANTY_START_DATE,
        ZIP
    } = useTranslation();
    const [hasNextPage, setHasNextPage] = useState(!!onNext);

    const labels = {
        application: APPLICATION,
        battery: BATTERY,
        boundary: BOUNDARY,
        chemical: CHEMICAL,
        city: CITY,
        classification: CLASSIFICATION,
        client: CLIENT,
        contactName: CONTACT_NAME,
        controllerSerialNumber: CONTROLLER_SERIAL_NUMBER,
        crop: CROP,
        drone: DRONE,
        droneSerialNumber: DRONE_SERIAL_NUMBER,
        email: EMAIL,
        epaNumber: EPA_NUMBER,
        estimatedAcres: ESTIMATED_ACRES,
        faaRegistrationNumber: FAA_REGISTRATION_NUMBER,
        farm: FARM,
        field: FIELD,
        flightControllerFirmwareVersion: FLIGHT_CONTROLLER_FIRMWARE_VERSION,
        flightHours: FLIGHT_HOURS,
        formulation: FORMULATION,
        manufacturer: MANUFACTURER,
        mix: MIX,
        modeOfAction: MODE_OF_ACTION,
        numberOfFlights: NUMBER_OF_FLIGHTS,
        phone: PHONE,
        pilot: PILOT,
        ppe: PPE,
        ppeReentry: PPE_REENTRY,
        rainfastness: RAINFASTNESS,
        rei: REI,
        role: ROLE,
        scheduledPilot: SCHEDULED_PILOT,
        serialNumber: SERIAL_NUMBER,
        signalWord: SIGNAL_WORD,
        source: 'Source',
        state: STATE,
        status: STATUS,
        targetPest: TARGET_PEST,
        totalAcres: TOTAL_ACRES,
        totalAreaApplied: TOTAL_AREA_APPLIED,
        totalVolumeApplied: TOTAL_VOLUME_APPLIED,
        user: USER,
        warrantyEndDate: WARRANTY_END_DATE,
        warrantyRemaining: WARRANTY_REMAINING,
        warrantyStartDate: WARRANTY_START_DATE,
        zip: ZIP
    };

    const fetchNext = async () => {
        if (!onNext) {
            return;
        }

        const { done } = await onNext();

        setHasNextPage(!done);
    };

    const count = totalCount || items?.length || 0;
    const label = labels[entityType];
    const entityTypeLabel = typeof label === 'function' ? label({ count }) : label;

    if (count === 1) {
        const { entityName, entityRoute } = items?.[0] ?? {};

        return (
            <DetailsViewEntityLink
                entityName={entityName ?? ''}
                entityRoute={entityRoute ?? ''}
                entityType={entityType}
            />
        );
    }

    return (
        <Wrapper className={`${className} ${styles.detailsViewEntityMenu}`} testId={testId}>
            <CaptionText className={styles.captionText} text={`${entityTypeLabel}:`} />

            {count === 0 && isLoading ? (
                <SkeletonText lines={1} />
            ) : count > 0 ? (
                <EntityMenu
                    count={count}
                    entityTypeLabel={entityTypeLabel}
                    fetchNext={fetchNext}
                    hasNextPage={hasNextPage}
                    items={items}
                />
            ) : null}
        </Wrapper>
    );
};

export default DetailsViewEntityMenu;
